import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { formatError } from "@/components/shared/helpers";
import { Pill } from "@/components/shared/Pill";
import Pagination, { usePagination } from "@/components/shared/Pagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function AdminInvoices({ properties }) {
  const [uploadPropertyId, setUploadPropertyId] = useState("");
  const [uploadEntity, setUploadEntity] = useState("BDM");
  const [filterPropertyId, setFilterPropertyId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchNumber, setSearchNumber] = useState("");
  const [searchPeriod, setSearchPeriod] = useState("");
  const [periodMonth, setPeriodMonth] = useState("");
  const [periodYear, setPeriodYear] = useState(() => new Date().getFullYear().toString());
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: "",
    issue_date: "",
    amount_ttc: "",
    paid: false,
    paid_date: "",
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [invoices, setInvoices] = useState([]);

  // ─── Tri & filtres colonne (client-side, aucune requête backend supplémentaire) ───
  // sortState.column ∈ {"number","property","period","amount"} (colonnes triables)
  // sortState.direction ∈ {"asc","desc","default"} — cycle : asc → desc → default (retour tri N° desc)
  // isDefault = true tant qu'aucun clic utilisateur n'a été appliqué (permet de distinguer 'default N° desc' d'un 'desc explicite' quand la colonne cliquée est N°).
  const DEFAULT_SORT = { column: "number", direction: "desc", isDefault: true };
  const [sortState, setSortState] = useState(DEFAULT_SORT);
  // Filtres cycliques cliquables (client-side)
  const [entityFilter, setEntityFilter] = useState("all"); // all | BDM | BDM_PARTICULIER
  const [paidFilter, setPaidFilter] = useState("all"); // all | paid | unpaid

  const cycleSort = (column) => {
    setSortState((prev) => {
      if (prev.column !== column) return { column, direction: "asc", isDefault: false };
      if (prev.isDefault) return { column, direction: "asc", isDefault: false };
      if (prev.direction === "asc") return { column, direction: "desc", isDefault: false };
      // desc explicite → retour défaut
      return DEFAULT_SORT;
    });
  };
  const cycleEntity = () => {
    setEntityFilter((v) => (v === "all" ? "BDM" : v === "BDM" ? "BDM_PARTICULIER" : "all"));
  };
  const cyclePaid = () => {
    setPaidFilter((v) => (v === "all" ? "paid" : v === "paid" ? "unpaid" : "all"));
  };
  const sortIndicator = (column) => {
    if (sortState.column !== column || sortState.direction === "default") return "";
    return sortState.direction === "asc" ? " ↑" : " ↓";
  };
  const filterIndicator = (activeLabel) => (activeLabel ? ` · ${activeLabel}` : "");

  // Applique filtres puis tri côté client sur la liste retournée par le backend
  const displayedInvoices = useMemo(() => {
    let arr = invoices.slice();
    if (entityFilter !== "all") arr = arr.filter((i) => (i.entity || "BDM") === entityFilter);
    if (paidFilter === "paid") arr = arr.filter((i) => !!i.paid);
    else if (paidFilter === "unpaid") arr = arr.filter((i) => !i.paid);
    const { column, direction } = sortState;
    const dir = direction === "asc" ? 1 : direction === "desc" ? -1 : 0;
    if (dir !== 0) {
      const getKey = (inv) => {
        if (column === "number") {
          // Tri numérique intelligent : extraire le nombre du n° facture ; fallback string
          const m = String(inv.invoice_number || "").match(/(\d+)/g);
          return m ? parseInt(m[m.length - 1], 10) : Number.NEGATIVE_INFINITY;
        }
        if (column === "property") return String(inv.property_short_name || inv.property_id || "").toLowerCase();
        if (column === "amount") return Number(inv.amount_ttc || 0);
        if (column === "period") {
          // Convertit "Mai 2026" en date sortable
          const s = String(inv.period_label || "");
          const MONTHS = { janvier: 1, février: 2, fevrier: 2, mars: 3, avril: 4, mai: 5, juin: 6, juillet: 7, août: 8, aout: 8, septembre: 9, octobre: 10, novembre: 11, décembre: 12, decembre: 12 };
          const parts = s.toLowerCase().trim().split(/\s+/);
          if (parts.length >= 2 && MONTHS[parts[0]]) {
            const y = parseInt(parts[1], 10) || 0;
            return y * 100 + MONTHS[parts[0]];
          }
          return s;
        }
        return "";
      };
      arr.sort((a, b) => {
        const ka = getKey(a); const kb = getKey(b);
        if (ka < kb) return -1 * dir;
        if (ka > kb) return 1 * dir;
        return 0;
      });
    }
    return arr;
  }, [invoices, entityFilter, paidFilter, sortState]);


  // ─── Onglets simplifiés : import / list ───────────────────────────────
  const [activeTab, setActiveTab] = useState("list"); // "import" | "list"

  // Pagination — reset à la page 1 quand les filtres ou la liste changent
  const invoicesFilterKey = `${filterPropertyId}|${statusFilter}|${searchNumber}|${searchPeriod}|${entityFilter}|${paidFilter}|${sortState.column}|${sortState.direction}|${displayedInvoices.length}`;
  const { page: invPage, setPage: setInvPage, pageSize: invPageSize, setPageSize: setInvPageSize, paginated: paginatedInvoices, total: invTotal } = usePagination(displayedInvoices, { defaultPageSize: 20, resetKey: invoicesFilterKey });
  const downloadInvoice = (invoiceId) => {
    // Récupère un blob authentifié puis ouvre dans un nouvel onglet
    api.get(`/admin/invoices/${invoiceId}/download`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        // Libération différée pour laisser le temps au navigateur d'ouvrir
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      })
      .catch((e) => toast({ title: "Erreur téléchargement", description: formatError(e), variant: "destructive" }));
  };

  const months = [
    { value: "01", label: "Janvier" },
    { value: "02", label: "Février" },
    { value: "03", label: "Mars" },
    { value: "04", label: "Avril" },
    { value: "05", label: "Mai" },
    { value: "06", label: "Juin" },
    { value: "07", label: "Juillet" },
    { value: "08", label: "Août" },
    { value: "09", label: "Septembre" },
    { value: "10", label: "Octobre" },
    { value: "11", label: "Novembre" },
    { value: "12", label: "Décembre" },
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) {
    years.push(y.toString());
  }

  const getPeriodLabel = () => {
    if (!periodMonth) return "";
    const monthObj = months.find(m => m.value === periodMonth);
    return monthObj ? `${monthObj.label} ${periodYear}` : "";
  };

  const loadInvoices = async () => {
    try {
      const params = {};
      if (filterPropertyId) params.property_id = filterPropertyId;
      if (statusFilter) params.status = statusFilter;
      if (searchNumber) params.invoice_number = searchNumber;
      if (searchPeriod) params.period_label = searchPeriod;
      
      const res = await api.get("/admin/invoices", { params });
      setInvoices(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPropertyId, statusFilter]);

  const togglePaidStatus = async (invoiceId, currentPaid) => {
    try {
      await api.put(`/admin/invoices/${invoiceId}/status?paid=${!currentPaid}`);
      toast({ title: currentPaid ? "Facture marquée non soldée" : "Facture marquée soldée" });
      await loadInvoices();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  const sendReminder = async (invoiceId) => {
    try {
      const res = await api.post(`/admin/invoices/${invoiceId}/send-reminder`);
      toast({ title: "Relance envoyée", description: `Email envoyé à ${res.data.sent_to}` });
      await loadInvoices();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  const sendNotification = async (invoiceId) => {
    try {
      const res = await api.post(`/admin/invoices/${invoiceId}/send-notification`);
      toast({ title: "Notification envoyée", description: `Email envoyé à ${res.data.sent_to || "propriétaire"}` });
      await loadInvoices();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    }
  };

  // ─── Suppression facture via AlertDialog ──────────────────────────────────
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);  const askDeleteInvoice = (inv) => setInvoiceToDelete(inv);
  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    const inv = invoiceToDelete;
    setInvoiceToDelete(null);
    try {
      await api.delete(`/admin/invoices/${inv.id}`);
      setInvoices((prev) => prev.filter((i) => i.id !== inv.id));
      toast({ title: "Facture supprimée", description: `${inv.invoice_number || inv.id} supprimée définitivement.` });
    } catch (e) {
      toast({ title: "Suppression impossible", description: formatError(e), variant: "destructive" });
    }
  };

  const deleteInvoice = async (inv) => {
    askDeleteInvoice(inv);
  };

  // ─── Édition facture ──────────────────────────────────────────────────────
  const [editInvoice, setEditInvoice] = useState(null);
  const [editForm, setEditForm] = useState({ invoice_number: "", period_label: "", amount_ttc: "", paid: false });
  const [editSaving, setEditSaving] = useState(false);

  const openEditInvoice = (inv) => {
    setEditInvoice(inv);
    setEditForm({
      invoice_number: inv.invoice_number || "",
      period_label: inv.period_label || "",
      amount_ttc: inv.amount_ttc != null ? String(inv.amount_ttc) : "",
      paid: !!inv.paid,
    });
  };

  const saveEditInvoice = async () => {
    if (!editInvoice) return;
    setEditSaving(true);
    try {
      const payload = {
        invoice_number: editForm.invoice_number,
        period_label: editForm.period_label,
        amount_ttc: Number(editForm.amount_ttc || 0),
        paid: !!editForm.paid,
      };
      await api.put(`/admin/invoices/${editInvoice.id}`, payload);
      toast({ title: "Facture mise à jour", description: editForm.invoice_number || editInvoice.id });
      setEditInvoice(null);
      await loadInvoices();
    } catch (e) {
      toast({ title: "Erreur", description: formatError(e), variant: "destructive" });
    } finally {
      setEditSaving(false);
    }
  };

  const formatDateEU = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    } catch {
      return iso;
    }
  };

  const upload = async () => {
    if (!uploadPropertyId) {
      toast({ title: "Logement manquant", description: "Choisissez un logement.", variant: "destructive" });
      return;
    }
    if (!pdfFile) {
      toast({ title: "PDF manquant", description: "Ajoutez le PDF.", variant: "destructive" });
      return;
    }

    const meta = {
      property_id: uploadPropertyId,
      invoice_number: invoiceForm.invoice_number,
      issue_date: invoiceForm.issue_date,
      period_label: getPeriodLabel(),
      amount_ttc: Number(invoiceForm.amount_ttc || 0),
      paid: !!invoiceForm.paid,
      paid_date: invoiceForm.paid ? (invoiceForm.paid_date || null) : null,
      entity: uploadEntity || "BDM",
    };

    const form = new FormData();
    form.append("meta_json", JSON.stringify(meta));
    form.append("pdf", pdfFile);

    try {
      await api.post("/admin/invoices", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Facture importée" });
      setPdfFile(null);
      setInvoiceForm({ invoice_number: "", issue_date: "", amount_ttc: "", paid: false, paid_date: "" });
      setUploadEntity("BDM");
      setPeriodMonth("");
      setPeriodYear(new Date().getFullYear().toString());
      await loadInvoices();
    } catch (e) {
      toast({ title: "Erreur import", description: formatError(e), variant: "destructive" });
    }
  };

  return (
    <div data-testid="admin-invoices" className="space-y-4">
      {/* Onglets */}
      <div data-testid="admin-invoices-tabs" className="flex flex-wrap gap-2">
        <Button data-testid="admin-invoices-tab-list" variant={activeTab === "list" ? "default" : "outline"} className={activeTab === "list" ? "rounded-full bg-violet-500 text-white hover:bg-violet-400" : "rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"} onClick={() => setActiveTab("list")}>
          📋 Factures ({invoices.length})
        </Button>
        <Button data-testid="admin-invoices-tab-import" variant={activeTab === "import" ? "default" : "outline"} className={activeTab === "import" ? "rounded-full bg-violet-500 text-white hover:bg-violet-400" : "rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10"} onClick={() => setActiveTab("import")}>
          ⬆️ Importer PDF
        </Button>
      </div>

      {activeTab === "import" && (
        /* Import Section */
        <Card data-testid="admin-invoices-upload-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="admin-invoices-upload-title" className="text-slate-50">Importer une facture (PDF)</CardTitle>
          <div data-testid="admin-invoices-upload-subtitle" className="text-sm text-slate-200">
            La facture est rattachée à un logement (un propriétaire peut en avoir plusieurs).
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label data-testid="admin-invoice-property-label">Logement</Label>
            <select
              data-testid="admin-invoice-property-select"
              className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
              value={uploadPropertyId}
              onChange={(e) => setUploadPropertyId(e.target.value)}
            >
              <option value="" data-testid="admin-invoice-property-option-empty">Choisir…</option>
              {properties.filter((p) => !p.archived).map((p) => (
                <option key={p.id} value={p.id} data-testid={`admin-invoice-property-option-${p.id}`}>
                  {p.short_name}
                </option>
              ))}
            </select>
          </div>

          {/* Sélecteur d'entité émettrice — visible uniquement si le logement est éligible BDMP (case « nom propre » cochée). Sinon BDM imposé silencieusement. */}
          {(() => {
            const prop = properties.find((p) => p.id === uploadPropertyId);
            const eligible = !!(prop && prop.pricing && prop.pricing.owned_by_individual);
            if (!eligible) return null;
            return (
              <div className="space-y-1">
                <Label data-testid="admin-invoice-entity-label">Émis par</Label>
                <select
                  data-testid="admin-invoice-entity-select"
                  className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                  value={uploadEntity}
                  onChange={(e) => setUploadEntity(e.target.value)}
                >
                  <option value="BDM" data-testid="admin-invoice-entity-option-bdm">BDM (Blanc des Montagnes)</option>
                  <option value="BDM_PARTICULIER" data-testid="admin-invoice-entity-option-bdmp">BDMP (Blanc des Montagnes Particulier)</option>
                </select>
                <div className="text-xs text-amber-300">Ce logement est éligible aux 2 entités — choix obligatoire avant import.</div>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="space-y-1">
              <Label data-testid="admin-invoice-number-label">N° facture</Label>
              <Input data-testid="admin-invoice-number-input" className="border-white/10 bg-white/[0.08]" value={invoiceForm.invoice_number} onChange={(e) => setInvoiceForm((s) => ({ ...s, invoice_number: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label data-testid="admin-invoice-date-label">Date</Label>
              <Input data-testid="admin-invoice-date-input" type="date" className="border-white/10 bg-white/[0.08]" value={invoiceForm.issue_date} onChange={(e) => setInvoiceForm((s) => ({ ...s, issue_date: e.target.value }))} />
            </div>
          </div>
          
          <div className="space-y-1">
            <Label data-testid="admin-invoice-period-label">Période</Label>
            <div className="grid grid-cols-2 gap-2">
              <select
                data-testid="admin-invoice-period-month"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
              >
                <option value="">Mois...</option>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                data-testid="admin-invoice-period-year"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={periodYear}
                onChange={(e) => setPeriodYear(e.target.value)}
              >
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="space-y-1">
              <Label data-testid="admin-invoice-amount-label">Montant TTC</Label>
              <Input data-testid="admin-invoice-amount-input" type="number" min={0} className="border-white/10 bg-white/[0.08]" value={invoiceForm.amount_ttc} onChange={(e) => setInvoiceForm((s) => ({ ...s, amount_ttc: e.target.value }))} />
            </div>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/[0.08] p-3">
            <div className="flex items-center justify-between">
              <div data-testid="admin-invoice-paid-label" className="text-sm text-slate-50">Facture soldée ?</div>
              <Checkbox data-testid="admin-invoice-paid-checkbox" checked={!!invoiceForm.paid} onCheckedChange={(v) => setInvoiceForm((s) => ({ ...s, paid: !!v }))} />
            </div>
            {invoiceForm.paid && (
              <div className="mt-2 space-y-1">
                <Label data-testid="admin-invoice-paid-date-label" className="text-xs text-slate-200">Date paiement</Label>
                <Input data-testid="admin-invoice-paid-date-input" type="date" className="border-white/10 bg-white/[0.08]" value={invoiceForm.paid_date} onChange={(e) => setInvoiceForm((s) => ({ ...s, paid_date: e.target.value }))} />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Label data-testid="admin-invoice-pdf-label">PDF</Label>
            <Input data-testid="admin-invoice-pdf-input" type="file" accept="application/pdf" className="border-white/10 bg-white/[0.08]" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} />
          </div>

          <div className="flex justify-start">
            <Button data-testid="admin-invoice-upload-button" className="rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={upload}>
              Importer
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {activeTab === "list" && (
      <>
      {/* Filters Section */}
      <Card data-testid="admin-invoices-filters-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="admin-invoices-filters-title" className="text-slate-50">Filtrer les factures</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="space-y-1">
              <Label>Logement</Label>
              <select
                data-testid="admin-invoice-filter-property"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={filterPropertyId}
                onChange={(e) => setFilterPropertyId(e.target.value)}
              >
                <option value="">Tous</option>
                {properties.filter((p) => !p.archived).map((p) => (
                  <option key={p.id} value={p.id}>{p.short_name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Statut</Label>
              <select
                data-testid="admin-invoice-filter-status"
                className="flex h-9 w-full rounded-md border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-slate-50"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous</option>
                <option value="paid">Soldée</option>
                <option value="unpaid">Non soldée</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>N° facture</Label>
              <Input
                data-testid="admin-invoice-filter-number"
                className="border-white/10 bg-white/[0.08]"
                placeholder="Rechercher..."
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadInvoices()}
              />
            </div>
            <div className="space-y-1">
              <Label>Période</Label>
              <Input
                data-testid="admin-invoice-filter-period"
                className="border-white/10 bg-white/[0.08]"
                placeholder="Ex: Février"
                value={searchPeriod}
                onChange={(e) => setSearchPeriod(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadInvoices()}
              />
            </div>
            <div className="space-y-1">
              <Label>&nbsp;</Label>
              <Button
                data-testid="admin-invoice-filter-search"
                className="w-full rounded-full bg-violet-500 text-white hover:bg-violet-400"
                onClick={loadInvoices}
              >
                Filtrer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List Section */}
      <Card data-testid="admin-invoices-list-card" className="border-white/10 bg-white/[0.08]">
        <CardHeader>
          <CardTitle data-testid="admin-invoices-list-title" className="text-slate-50">Factures importées</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-xl border border-white/10 bg-white/[0.08]">
            <Table data-testid="admin-invoices-table">
              <TableHeader>
                <TableRow>
                  <TableHead data-testid="admin-invoices-col-number">
                    <button data-testid="admin-invoices-sort-number" type="button" onClick={() => cycleSort("number")} className="inline-flex items-center gap-1 font-medium text-slate-200 hover:text-slate-50" title="Trier par N° (asc / desc / défaut)">
                      N°{sortIndicator("number")}
                    </button>
                  </TableHead>
                  <TableHead data-testid="admin-invoices-col-property">
                    <button data-testid="admin-invoices-sort-property" type="button" onClick={() => cycleSort("property")} className="inline-flex items-center gap-1 font-medium text-slate-200 hover:text-slate-50" title="Trier par logement (A→Z / Z→A / défaut)">
                      Logement{sortIndicator("property")}
                    </button>
                  </TableHead>
                  <TableHead data-testid="admin-invoices-col-entity">
                    <button data-testid="admin-invoices-filter-entity" type="button" onClick={cycleEntity} className="inline-flex items-center gap-1 font-medium text-slate-200 hover:text-slate-50" title="Filtrer par émetteur (tous / BDM / BDMP)">
                      Émetteur{filterIndicator(entityFilter === "BDM" ? "BDM" : entityFilter === "BDM_PARTICULIER" ? "BDMP" : "")}
                    </button>
                  </TableHead>
                  <TableHead data-testid="admin-invoices-col-period">
                    <button data-testid="admin-invoices-sort-period" type="button" onClick={() => cycleSort("period")} className="inline-flex items-center gap-1 font-medium text-slate-200 hover:text-slate-50" title="Trier par période (chrono / anti-chrono / défaut)">
                      Période{sortIndicator("period")}
                    </button>
                  </TableHead>
                  <TableHead data-testid="admin-invoices-col-amount">
                    <button data-testid="admin-invoices-sort-amount" type="button" onClick={() => cycleSort("amount")} className="inline-flex items-center gap-1 font-medium text-slate-200 hover:text-slate-50" title="Trier par montant (asc / desc / défaut)">
                      Montant{sortIndicator("amount")}
                    </button>
                  </TableHead>
                  <TableHead data-testid="admin-invoices-col-paid">
                    <button data-testid="admin-invoices-filter-paid" type="button" onClick={cyclePaid} className="inline-flex items-center gap-1 font-medium text-slate-200 hover:text-slate-50" title="Filtrer par statut (tous / soldée / non soldée)">
                      Statut{filterIndicator(paidFilter === "paid" ? "Soldée" : paidFilter === "unpaid" ? "Non soldée" : "")}
                    </button>
                  </TableHead>
                  <TableHead data-testid="admin-invoices-col-actions">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedInvoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`admin-invoice-row-${inv.id}`}>
                    <TableCell data-testid={`admin-invoice-number-${inv.id}`}>
                      {inv.invoice_number || "-"}
                      {inv.generated && (<span data-testid={`admin-invoice-generated-badge-${inv.id}`} title="Facture générée automatiquement" className="ml-2 inline-block rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] font-semibold text-violet-200 align-middle">Auto</span>)}
                    </TableCell>
                    <TableCell data-testid={`admin-invoice-property-${inv.id}`}>{inv.property_short_name || inv.property_id}</TableCell>
                    <TableCell data-testid={`admin-invoice-entity-${inv.id}`}>
                      {inv.entity === "BDM_PARTICULIER" ? (
                        <span className="inline-flex items-center rounded-full bg-fuchsia-500/15 text-fuchsia-200 border border-fuchsia-400/30 px-2 py-0.5 text-xs font-medium">BDMP</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-violet-500/15 text-violet-200 border border-violet-400/30 px-2 py-0.5 text-xs font-medium">BDM</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`admin-invoice-period-${inv.id}`}>{inv.period_label || "-"}</TableCell>
                    <TableCell data-testid={`admin-invoice-amount-${inv.id}`}>{inv.amount_ttc} €</TableCell>
                    <TableCell data-testid={`admin-invoice-paid-${inv.id}`}>
                      {inv.paid ? <Pill testId={`admin-invoice-paid-pill-${inv.id}`} tone="emerald">Soldée</Pill> : <Pill testId={`admin-invoice-unpaid-pill-${inv.id}`} tone="rose">Non soldée</Pill>}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          data-testid={`admin-invoice-download-${inv.id}`}
                          variant="outline"
                          size="sm"
                          className="border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10 text-xs"
                          onClick={() => downloadInvoice(inv.id)}
                          title="Télécharger le PDF"
                        >
                          ⬇️ Télécharger
                        </Button>
                        <Button
                          data-testid={`admin-invoice-toggle-${inv.id}`}
                          variant="outline"
                          size="sm"
                          className="border-white/10 bg-white/[0.08] text-slate-50 hover:bg-white/10 text-xs"
                          onClick={() => togglePaidStatus(inv.id, inv.paid)}
                        >
                          {inv.paid ? "Marquer non soldée" : "Marquer soldée"}
                        </Button>
                        <Button
                          data-testid={`admin-invoice-notify-${inv.id}`}
                          variant="outline"
                          size="sm"
                          disabled={!!inv.notification_sent_at}
                          title={inv.notification_sent_at ? `Notification déjà envoyée le ${formatDateEU(inv.notification_sent_at)}` : "Envoyer la notification d'arrivée au propriétaire"}
                          className={`text-xs ${inv.notification_sent_at ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 cursor-default" : "border-violet-500/40 bg-violet-500/10 text-violet-200 hover:bg-violet-500/20"}`}
                          onClick={() => !inv.notification_sent_at && sendNotification(inv.id)}
                        >
                          {inv.notification_sent_at ? "✅ Notifié" : "📧 Notifier"}
                        </Button>
                        {!inv.paid && (
                          <Button
                            data-testid={`admin-invoice-reminder-${inv.id}`}
                            variant="outline"
                            size="sm"
                            title={inv.reminder_sent_at ? `Relance déjà envoyée le ${formatDateEU(inv.reminder_sent_at)}` : "Envoyer une relance par email"}
                            className={`text-xs ${inv.reminder_sent_at ? "border-white/10 bg-white/[0.04] text-slate-400 hover:bg-white/[0.06]" : "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"}`}
                            onClick={() => sendReminder(inv.id)}
                          >
                            📧 Relancer
                          </Button>
                        )}
                        <Button
                          data-testid={`admin-invoice-edit-${inv.id}`}
                          variant="outline"
                          size="sm"
                          title="Modifier les informations de la facture"
                          className="text-xs border-blue-500/40 bg-blue-500/10 text-blue-200 hover:bg-blue-500/20"
                          onClick={() => openEditInvoice(inv)}
                        >
                          ✏️ Modifier
                        </Button>
                        <Button
                          data-testid={`admin-invoice-delete-${inv.id}`}
                          variant="outline"
                          size="sm"
                          title="Supprimer définitivement cette facture"
                          className="text-xs border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20"
                          onClick={() => deleteInvoice(inv)}
                        >
                          🗑️ Supprimer
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!displayedInvoices.length && (
                  <TableRow>
                    <TableCell data-testid="admin-invoices-empty" colSpan={7} className="py-8 text-center text-slate-200">
                      Aucune facture.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={invPage}
            pageSize={invPageSize}
            total={invTotal}
            onPageChange={setInvPage}
            onPageSizeChange={setInvPageSize}
            testIdPrefix="admin-invoices-pagination"
          />
        </CardContent>
      </Card>
      </>
      )}

      {/* AlertDialog de confirmation de suppression facture */}
      <AlertDialog open={!!invoiceToDelete} onOpenChange={(o) => !o && setInvoiceToDelete(null)}>
        <AlertDialogContent data-testid="admin-invoice-delete-dialog" className="border-white/10 bg-[#0f1320] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="admin-invoice-delete-title" className="text-slate-50">
              Supprimer définitivement cette facture ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300 space-y-2">
              <span className="block">Cette action est <strong className="text-rose-300">irréversible</strong>.</span>
              {invoiceToDelete?.invoice_number && (
                <span data-testid="admin-invoice-delete-number" className="block text-slate-200">
                  Facture <strong>{invoiceToDelete.invoice_number}</strong>
                  {invoiceToDelete.amount_ttc ? ` — ${Number(invoiceToDelete.amount_ttc).toFixed(2)} € TTC` : ""}
                </span>
              )}
              {invoiceToDelete?.notification_sent_at && (
                <span data-testid="admin-invoice-delete-sent-warning" className="block rounded-md border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-amber-100">
                  ⚠️ Cette facture a déjà été envoyée au propriétaire (le {formatDateEU(invoiceToDelete.notification_sent_at)}). Confirmer quand même la suppression ?
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-invoice-delete-cancel" className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction data-testid="admin-invoice-delete-confirm" onClick={confirmDeleteInvoice} className="bg-rose-500 text-white hover:bg-rose-400">
              🗑️ Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modale d'édition facture */}
      <Dialog open={!!editInvoice} onOpenChange={(o) => !o && setEditInvoice(null)}>
        <DialogContent data-testid="admin-invoice-edit-dialog" className="border-white/10 bg-[#0f1320] text-slate-100 max-w-md">
          <DialogHeader>
            <DialogTitle data-testid="admin-invoice-edit-title" className="text-slate-50">
              Modifier la facture
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editInvoice?.property_short_name ? `Logement : ${editInvoice.property_short_name}` : "Mettre à jour les informations de la facture."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label data-testid="admin-invoice-edit-number-label" className="text-xs text-slate-300">Numéro de facture</Label>
              <Input
                data-testid="admin-invoice-edit-number-input"
                className="border-white/10 bg-white/[0.08]"
                value={editForm.invoice_number}
                onChange={(e) => setEditForm((s) => ({ ...s, invoice_number: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label data-testid="admin-invoice-edit-period-label" className="text-xs text-slate-300">Période</Label>
              <Input
                data-testid="admin-invoice-edit-period-input"
                className="border-white/10 bg-white/[0.08]"
                value={editForm.period_label}
                placeholder="Ex: Mai 2026"
                onChange={(e) => setEditForm((s) => ({ ...s, period_label: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label data-testid="admin-invoice-edit-amount-label" className="text-xs text-slate-300">Montant TTC (€)</Label>
              <Input
                data-testid="admin-invoice-edit-amount-input"
                type="number"
                step="0.01"
                min={0}
                className="border-white/10 bg-white/[0.08]"
                value={editForm.amount_ttc}
                onChange={(e) => setEditForm((s) => ({ ...s, amount_ttc: e.target.value }))}
              />
            </div>
            <div className="rounded-md border border-white/10 bg-white/[0.04] px-3 py-2">
              <label htmlFor="admin-invoice-edit-paid-checkbox" className="flex cursor-pointer items-center justify-between">
                <div>
                  <div className="text-sm text-slate-100">Facture soldée</div>
                  <div className="text-xs text-slate-400">Cocher si la facture a été payée par le propriétaire.</div>
                </div>
                <Checkbox
                  id="admin-invoice-edit-paid-checkbox"
                  data-testid="admin-invoice-edit-paid-checkbox"
                  checked={editForm.paid}
                  onCheckedChange={(v) => setEditForm((s) => ({ ...s, paid: !!v }))}
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="admin-invoice-edit-cancel" variant="outline" className="rounded-full border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10" onClick={() => setEditInvoice(null)}>
              Annuler
            </Button>
            <Button data-testid="admin-invoice-edit-save" disabled={editSaving} className="rounded-full bg-violet-500 text-white hover:bg-violet-400" onClick={saveEditInvoice}>
              {editSaving ? "Enregistrement…" : "💾 Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
